import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../components/layout/layout';
import {
  Search,
  Target,
  Shield,
  CheckCircle,
  CreditCard,
  Compass,
  Building2,
  Clock,
  Briefcase,
  Brain,
  Handshake,
  Award,
  FileSearch,
  Route,
  Users,
} from 'lucide-react';
import Section from '../components/ui/section';
import StatsSection from '../components/sections/stats-section';
import SolutionsSection from '../components/sections/solutions-section';
import ArticlesSection from '../components/sections/articles-section';
import HubSpotModal from '../components/ui/hubspot-modal';
import HeroTemplateEnts from '../components/sections/HeroTemplateEnts';
import SectionBackground from '../components/sections/section-background';
import ClientLogosWall from '../components/sections/ClientLogosWall';
import CandidateTestimonial from '../components/sections/CandidateTestimonial';
import LinkedInRecommendations from '../components/sections/LinkedInRecommendations';
import CabinetComparisonSection from '../components/sections/CabinetComparisonSection';
import { WebPageSchema } from '../lib/utils/schema-utils';

// Typography system — 3 levels max, consistent across all sections
// L1: Section titles (h2) — large, bold, near-black, gradient on key phrase
// L2: Card/block titles (h3) — medium, semibold, near-black
// L3: Body/description — regular, neutral-600

const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

// L1 — section titles
const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';

// L2 — card/block titles
const h3Class =
  'text-xl font-semibold text-[#30475e] mb-3 leading-snug';

// L3 — body text
const bodyClass =
  'text-base text-neutral-600 leading-relaxed';

// Legacy aliases kept for unchanged blocks
const sectionTitleClass = h2Class;
const sectionSubtitleClass = 'text-lg md:text-xl text-neutral-600 leading-relaxed';

const benefits = [
  {
    icon: Award,
    title: 'Expertise métier recrutement',
    description:
      'Nous recrutons depuis des années. Nous savons cadrer un besoin, lire un parcours, challenger un profil et sécuriser une décision d’embauche.',
  },
  {
    icon: Route,
    title: 'Onboarding candidat avant candidature',
    description:
      'Avant de postuler, le candidat découvre votre entreprise, votre culture, vos attentes et la réalité du poste. Il avance déjà mieux informé.',
  },
  {
    icon: Shield,
    title: 'Recrutement plus fiable',
    description:
      'Moins de candidatures mal alignées, moins d’entretiens inutiles, moins d’erreurs de casting. Vous gagnez du temps et vous recrutez mieux.',
  },
];

const expertiseItems = [
  {
    icon: Briefcase,
    title: 'Compréhension du besoin réel',
    description:
      'Nous ne partons pas d’une fiche de poste générique. Nous analysons le contexte, les enjeux, le management, l’équipe et les critères de réussite.',
  },
  {
    icon: FileSearch,
    title: 'Lecture fine des profils',
    description:
      'Un bon CV ne suffit pas. Nous évaluons la trajectoire, les choix de carrière, la cohérence du parcours, les motivations et les signaux faibles.',
  },
  {
    icon: Brain,
    title: 'Méthode augmentée',
    description:
      'Nous utilisons les bons outils, y compris l’IA, pour accélérer la recherche et enrichir l’analyse, sans remplacer le jugement humain.',
  },
  {
    icon: Handshake,
    title: 'Accompagnement candidat',
    description:
      'Le candidat est mieux informé, mieux accompagné et plus capable de se projeter. C’est aussi comme ça qu’on protège votre marque employeur.',
  },
];

const onboardingItems = [
  {
    number: '01',
    title: 'Découvrir l’entreprise',
    description:
      'Le candidat comprend votre histoire, votre culture, vos valeurs concrètes et votre manière de travailler.',
  },
  {
    number: '02',
    title: 'Comprendre le poste réel',
    description:
      'Il découvre les missions, les responsabilités, les contraintes, les objectifs et les critères de réussite.',
  },
  {
    number: '03',
    title: 'Se projeter dans l’environnement',
    description:
      'Il visualise l’équipe, le management, le rythme, les outils, le contexte business et la réalité du quotidien.',
  },
  {
    number: '04',
    title: 'Candidater avec intention',
    description:
      'Il postule avec plus de clarté, plus de maturité et une meilleure compréhension des attentes mutuelles.',
  },
];

const processSteps = [
  {
    icon: Building2,
    title: 'Immersion entreprise',
    description:
      'Nous cadrons votre culture, vos métiers, vos enjeux, vos attentes et les éléments qui permettront aux candidats de se projeter.',
  },
  {
    icon: Search,
    title: 'Analyse du besoin',
    description:
      'Nous clarifions le poste réel, les compétences attendues, le contexte managérial, les priorités et les critères de décision.',
  },
  {
    icon: Compass,
    title: 'Parcours d’onboarding candidat',
    description:
      'Nous construisons un parcours de découverte qui informe le candidat avant sa candidature et l’aide à mieux comprendre l’entreprise.',
  },
  {
    icon: Target,
    title: 'Sourcing et qualification',
    description:
      'Nous identifions les bons profils, analysons leur trajectoire et qualifions leur motivation, leur posture et leur alignement.',
  },
  {
    icon: CheckCircle,
    title: 'Short-list utile',
    description:
      'Vous recevez une sélection courte, argumentée et exploitable. Pas du volume pour faire joli. Des candidats qui ont du sens.',
  },
  {
    icon: CreditCard,
    title: 'Facturation au succès',
    description:
      'Vous ne payez qu’en cas de recrutement abouti. Notre modèle reste aligné avec votre résultat.',
  },
];

const methodPillars = [
  {
    title: 'Expertise métier',
    description:
      'Des années de recrutement, une vraie lecture des profils et une compréhension concrète des enjeux entreprise.',
  },
  {
    title: 'Onboarding candidat',
    description:
      'Un parcours de découverte qui informe, engage et pré-qualifie le candidat avant même sa candidature.',
  },
  {
    title: 'Technologie utile',
    description:
      'L’IA et les outils digitaux servent la méthode. Ils accélèrent la recherche, mais ne remplacent jamais le jugement humain.',
  },
];

export default function HomePage() {
  const [isHubSpotModalOpen, setIsHubSpotModalOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <>
      <Layout
        title="Cabinet recrutement Marseille IA · -73% no-show | recruteurs.io"
        description="Cabinet de recrutement Marseille augmenté IA. Plateforme Parcours Découverte avant signature, -73% no-show, +40% rétention 12 mois, garantie 6 mois opposable. Devis 24h."
        keywords="cabinet de recrutement marseille, cabinet recrutement aix-en-provence, cabinet recrutement paca, parcours découverte, recrutement ia, chasse de tête marseille, no-show recrutement, rétention candidat"
      >
        <WebPageSchema
          page={{
            title: 'Cabinet de recrutement Marseille · Plateforme Parcours Découverte | recruteurs.io',
            description:
              "recruteurs.io combine chasse de tête senior et IA appliquée au sourcing, et fait découvrir à chaque candidat la réalité de l'entreprise via une plateforme propriétaire. Le candidat se ré-engage en connaissance de cause : moins de no-show, moins de ruptures précoces, marque employeur scénarisée.",
            url: 'https://recruteurs.io',
            lastReviewed: '2026-05-09',
            speakable: {
              cssSelector: '.hero-content',
            },
          }}
        />

      <HeroTemplateEnts
          page="home"
          title="Une décision éclairée, un recrutement solide."
          eyebrow="Cabinet de recrutement innovant · Marseille · Aix-en-Provence · PACA"
          description="Cabinet de recrutement innovant à Marseille : avant de signer, vos candidats traversent notre Plateforme Parcours Découverte. Ils comprennent vraiment votre entreprise, votre poste, votre équipe, votre rémunération. Ils décident en connaissance de cause — et ils tiennent."
          imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/Cabinet-de-recrutement-marseille.webp"
          cta1={{
            label: 'Réserver un échange (15 min)',
            onClick: () => navigate('/reservation'),
          }}
          cta2={{
            label: 'Voir UN Parcours Découverte',
            onClick: () => navigate('/decouvrir'),
          }}
        />

        {/* Trust strip — marquee compact juste sous le hero pour réengager */}
        <ClientLogosWall
          variant="strip"
          page="home"
          section="home-trust-strip"
          className="bg-[#fbfaf8] border-y border-neutral-200"
        />

        {/* Value Proposition */}
        <SectionBackground page="home" section="home-value" className="bg-white">
          <Section>
            <div className="max-w-4xl mx-auto text-center mb-14">
              <span className={eyebrowClass}>
                Expertise métier + onboarding candidat
              </span>

              <h2 className={sectionTitleClass}>
                Nous ne faisons pas que trouver des candidats.{' '}
                <span className={gradientText}>Nous préparons leur décision.</span>
              </h2>

              <p className={sectionSubtitleClass}>
                Un bon recrutement repose sur deux exigences : une lecture experte du besoin côté entreprise,
                et un <strong>parcours candidat scénarisé</strong> côté postulant — un onboarding pré-embauche
                qui permet à chacun de décider en connaissance de cause. C'est cette combinaison qui réduit
                les erreurs, améliore l'alignement et accélère la décision.{' '}
                <a
                  href="/decouvrir"
                  className="font-semibold text-[#d03484] underline-offset-4 hover:underline whitespace-nowrap"
                >
                  Voir notre Plateforme Parcours Découverte →
                </a>
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {benefits.map((item, index) => {
                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.5, delay: index * 0.08 }}
                    className="group bg-white rounded-3xl p-8 border border-neutral-200 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-6 group-hover:scale-105 transition-transform duration-300">
                      <Icon className="w-7 h-7" />
                    </div>

                    <h3 className={h3Class}>{item.title}</h3>

                    <p className={bodyClass}>{item.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </Section>
        </SectionBackground>

        {/* Expertise Section */}
        <SectionBackground page="home" section="home-expertise" className="bg-[#fbfaf8]">
          <Section>
            <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-12 items-start">
              <div className="lg:sticky lg:top-24">
                <span className={eyebrowClass}>Notre socle métier</span>

                <h2 className={sectionTitleClass}>
                  Le meilleur de l’expertise recrutement, avec un process plus clair pour vous et vos candidats.
                </h2>

                <p className={sectionSubtitleClass}>
                  L’innovation n’a de valeur que si elle améliore le recrutement. Notre socle, c’est d’abord
                  l’expertise métier : comprendre un besoin, lire un profil, évaluer une trajectoire et sécuriser
                  une décision d’embauche. L’onboarding candidat vient renforcer cette expertise, pas la remplacer.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {expertiseItems.map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, y: 18 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: '-80px' }}
                      transition={{ duration: 0.5, delay: index * 0.08 }}
                      className="bg-white rounded-3xl p-7 border border-neutral-200 shadow-sm hover:shadow-lg transition-all duration-300"
                    >
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5">
                        <Icon className="w-6 h-6" />
                      </div>

                      <h3 className={h3Class}>{item.title}</h3>

                      <p className={bodyClass}>{item.description}</p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </Section>
        </SectionBackground>

        {/* Candidate Onboarding Section */}
        <SectionBackground
          page="home"
          section="candidate-onboarding"
          className="relative overflow-hidden bg-[#fbfaf8]"
        >
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-16 left-1/4 h-72 w-72 rounded-full bg-[#d03484]/10 blur-3xl" />
            <div className="absolute bottom-10 right-1/4 h-72 w-72 rounded-full bg-[#d47254]/10 blur-3xl" />
          </div>

          <Section>
            <div className="relative z-10">
              <div className="max-w-4xl mx-auto text-center mb-16">
                <span className={eyebrowClass}>
                  Onboarding candidat avant candidature
                </span>

                <h2 className={h2Class}>
                  Avant de postuler, le candidat doit déjà{' '}
                  <span className={gradientText}>comprendre et se projeter.</span>
                </h2>

                <p className="text-lg md:text-xl text-neutral-600 leading-relaxed max-w-3xl mx-auto">
                  Nous ne demandons pas aux candidats de postuler à l’aveugle. Nous construisons
                  des parcours de découverte qui leur permettent de comprendre votre entreprise,
                  votre culture, le poste et vos attentes avant même d’entrer dans le process.
                </p>
              </div>

              <div className="relative max-w-6xl mx-auto">
                <div className="hidden lg:block absolute left-0 right-0 top-[84px] h-px bg-gradient-to-r from-transparent via-neutral-300 to-transparent" />

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {onboardingItems.map((item, index) => (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: '-80px' }}
                      transition={{ duration: 0.6, delay: index * 0.08 }}
                      className="relative group"
                    >
                      <div className="relative bg-white rounded-[1.75rem] p-7 shadow-sm ring-1 ring-neutral-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl min-h-[300px]">
                        <div className="mb-7 flex items-center justify-between">
                          <div className="h-14 w-14 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white font-bold shadow-lg shadow-[#d03484]/20">
                            {item.number}
                          </div>

                          <div className="h-2 w-2 rounded-full bg-neutral-300 group-hover:bg-[#d03484] transition-colors duration-300" />
                        </div>

                        <h3 className={h3Class}>{item.title}</h3>

                        <p className={bodyClass}>{item.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="mt-14 max-w-5xl mx-auto">
                <div className="rounded-[2rem] bg-white p-8 md:p-10 shadow-sm ring-1 ring-neutral-200">
                  <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-8 items-center">
                    <div>
                      <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-3">
                        Résultat
                      </p>

                      <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-[#30475e] leading-tight">
                        Des candidats qui ne postulent pas{' '}
                        <span className={gradientText}>par hasard.</span>
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <div className="rounded-2xl bg-[#fbfaf8] p-5 ring-1 ring-neutral-200">
                        <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-2">
                          Côté candidat
                        </p>
                        <p className={`text-base font-semibold ${gradientText}`}>
                          Meilleure projection
                        </p>
                      </div>

                      <div className="rounded-2xl bg-[#fbfaf8] p-5 ring-1 ring-neutral-200">
                        <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-2">
                          Côté entreprise
                        </p>
                        <p className={`text-base font-semibold ${gradientText}`}>
                          Moins de perte de temps
                        </p>
                      </div>

                      <div className="rounded-2xl bg-[#fbfaf8] p-5 ring-1 ring-neutral-200">
                        <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-2">
                          Côté recrutement
                        </p>
                        <p className={`text-base font-semibold ${gradientText}`}>
                          Plus d’alignement
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Section>
        </SectionBackground>

        {/* Method Section */}
        <SectionBackground page="home" section="home-method" className="bg-white">
          <Section>
            <div className="max-w-4xl mx-auto text-center mb-14">
              <span className={eyebrowClass}>
                <a
                  href="/cabinet-recrutement-marseille"
                  className="hover:underline"
                >
                  cabinet de recrutement à Marseille
                </a>
                , intervention partout en France
              </span>

              <h2 className={h2Class}>
                Une méthode qui combine{' '}
                <span className={gradientText}>métier, onboarding et technologie.</span>
              </h2>

              <p className={sectionSubtitleClass}>
                Nous faisons ce métier depuis des années. Notre force : allier le meilleur
                de l’expertise recrutement à un process candidat plus clair, plus utile et plus engageant.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {methodPillars.map((pillar, index) => (
                <motion.div
                  key={pillar.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, delay: index * 0.08 }}
                  className="group rounded-[1.75rem] bg-white p-8 shadow-sm ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-8 flex items-center justify-between">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white font-bold shadow-lg shadow-[#d03484]/20">
                      0{index + 1}
                    </div>

                    <div className="h-2 w-2 rounded-full bg-neutral-300 group-hover:bg-[#d03484] transition-colors duration-300" />
                  </div>

                  <h3 className={h3Class}>{pillar.title}</h3>

                  <p className={bodyClass}>{pillar.description}</p>
                </motion.div>
              ))}
            </div>

            <div className="mt-12 rounded-[2rem] bg-[#fbfaf8] p-8 md:p-10 ring-1 ring-neutral-200">
              <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-8 items-center">
                <div>
                  <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-3">
                    Notre conviction
                  </p>

                  <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-[#30475e] leading-tight">
                    La technologie <span className={gradientText}>accelere.</span> L&apos;expertise decide. L&apos;onboarding aligne.
                  </h3>
                </div>

                <div className="space-y-4 text-base text-neutral-600 leading-relaxed">
                  <p>
                    Un recrutement réussi ne dépend pas seulement d’un bon sourcing ou d’un bon outil.
                    Il dépend d’une compréhension fine du besoin, d’une vraie qualification et d’une
                    expérience candidat sérieuse.
                  </p>

                  <p>
                    L’onboarding candidat permet de préparer la décision avant même la candidature.
                    L’expertise métier permet ensuite de qualifier, challenger et sécuriser le bon choix.
                  </p>
                </div>
              </div>
            </div>
          </Section>
        </SectionBackground>

        {/* Process Section */}
        <SectionBackground page="home" section="home-process" className="bg-[#fbfaf8]">
          <Section>
            <div className="max-w-4xl mx-auto text-center mb-14">
              <span className={eyebrowClass}>Un process lisible de bout en bout</span>

              <h2 className={sectionTitleClass}>
                Comment se déroule une mission avec{' '}
                <span className={gradientText}>Recruteurs.io ?</span>
              </h2>

              <p className={sectionSubtitleClass}>
                Un bon recrutement ne repose pas sur la chance. Il repose sur une méthode claire :
                comprendre, informer, sourcer, qualifier, présenter les bons profils.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {processSteps.map((step, index) => {
                const Icon = step.icon;

                return (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.5, delay: index * 0.06 }}
                    className="relative bg-white rounded-3xl p-7 shadow-sm border border-neutral-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                  >
                    <div className="flex items-start justify-between gap-4 mb-6">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white">
                        <Icon className="w-6 h-6" />
                      </div>

                      <div className="text-xs font-semibold text-neutral-400 uppercase tracking-widest">
                        Étape {index + 1}
                      </div>
                    </div>

                    <h3 className={h3Class}>{step.title}</h3>

                    <p className={bodyClass}>{step.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </Section>
        </SectionBackground>

        {/* Time Saving Section */}
        <SectionBackground page="home" section="home-time-saving" className="bg-white">
          <Section>
            <div className="bg-gradient-to-r from-[#d03484] to-[#d47254] rounded-[2rem] p-8 md:p-12 text-white shadow-2xl">
              <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-10 items-center">
                <div>
                  <div className="w-16 h-16 rounded-2xl bg-white/15 flex items-center justify-center mb-6">
                    <Clock className="w-8 h-8" />
                  </div>

                  <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-5 leading-[1.08]">
                    Moins d&apos;entretiens inutiles. <span className="text-white/80">Plus de candidats vraiment prets.</span>
                  </h2>

                  <p className="text-white/80 text-base leading-relaxed">
                    Le candidat arrive dans le process avec une vision plus claire de l’entreprise,
                    du poste et des attentes. Vous gagnez du temps, et lui aussi.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/15 rounded-3xl p-6 backdrop-blur-sm ring-1 ring-white/10">
                    <p className="text-3xl font-bold mb-3">1</p>
                    <p className="text-white/80 text-sm leading-relaxed">
                      Le candidat découvre l’entreprise avant de postuler.
                    </p>
                  </div>

                  <div className="bg-white/15 rounded-3xl p-6 backdrop-blur-sm ring-1 ring-white/10">
                    <p className="text-3xl font-bold mb-3">2</p>
                    <p className="text-white/80 text-sm leading-relaxed">
                      Il se projette dans le poste et son environnement réel.
                    </p>
                  </div>

                  <div className="bg-white/15 rounded-3xl p-6 backdrop-blur-sm ring-1 ring-white/10">
                    <p className="text-3xl font-bold mb-3">3</p>
                    <p className="text-white/80 text-sm leading-relaxed">
                      Il candidate avec plus d’intention, de clarté et d’alignement.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Section>
        </SectionBackground>

        {/* Solutions Section */}
        <SolutionsSection onOpenHubSpot={() => setIsHubSpotModalOpen(true)} />

        {/* Comparative cabinet classique vs recruteurs.io (5 axes condensés + lien vers /pourquoi-recruteurs-io) */}
        <CabinetComparisonSection />

        {/* Témoignage candidat — preuve sociale Parcours Découverte */}
        <CandidateTestimonial
          eyebrow="Témoignage candidat"
          sectionTitle={
            <>
              L'expérience candidat qu'on{' '}
              <span className={gradientText}>a toujours voulue.</span>
            </>
          }
          name="S. L."
          role="Développement commercial · 20 ans d'expérience"
          verified
          quote="Avec **plus de 20 ans** dans le développement commercial, j'ai eu affaire à de nombreux entretiens RH sous toutes les formes que l'on connaît. C'est **la première fois** que je note un parcours **interactif, clair et limpide** sur les tenants et aboutissants de l'entreprise qui recrute."
        />

        {/* Recommandations LinkedIn — preuve sociale tierce, breadth */}
        <LinkedInRecommendations />

        {/* Stats Section */}
        <SectionBackground page="home" section="home-stats" className="relative bg-white">
          <StatsSection
            title={
              <>
                <span className={gradientText}>Notre impact</span> en chiffres
              </>
            }
            subtitle="Des résultats concrets qui témoignent de notre méthode de recrutement"
          />
        </SectionBackground>

        {/* Articles Section */}
        <SectionBackground page="home" section="home-articles" className="relative bg-white">
          <ArticlesSection />
        </SectionBackground>
      </Layout>

      <HubSpotModal
        isOpen={isHubSpotModalOpen}
        onClose={() => setIsHubSpotModalOpen(false)}
      />
    </>
  );
}